'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import { Transformer } from 'markmap-lib';
import { Markmap } from 'markmap-view';
import { cn } from '@/lib/utils';
import { ZoomIn, ZoomOut, Maximize2, Info } from 'lucide-react';

interface MindmapProps {
  markdown: string;
  className?: string;
  darkMode?: boolean;
  onTimestampClick?: (timestamp: number, endTimestamp?: number) => void;
}

export default function Mindmap({ 
  markdown, 
  className,
  darkMode = false,
  onTimestampClick
}: MindmapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const mmRef = useRef<Markmap>();
  const [isLoading, setIsLoading] = useState(true);
  const observerRef = useRef<MutationObserver | null>(null);

  // Format the markdown to enhance timestamp display
  const enhancedMarkdown = markdown.replace(
    /\[timestamp:\s*(\d+(?:\.\d+)?)\]/g, 
    (match, seconds) => {
      // Convert seconds to mm:ss format
      const totalSeconds = parseFloat(seconds);
      const minutes = Math.floor(totalSeconds / 60);
      const remainingSeconds = Math.floor(totalSeconds % 60);
      const timeString = `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
      return ` [[${timeString}]](#timestamp-${totalSeconds})`;
    }
  );

  // 辅助函数：从链接中提取时间戳
  const extractTimestampFromHref = (href: string): number | null => {
    if (href.startsWith('#timestamp-')) {
      const timestampStr = href.replace('#timestamp-', '');
      const timestamp = parseFloat(timestampStr);
      return !isNaN(timestamp) ? timestamp : null;
    }
    return null;
  };

  // 辅助函数：处理时间戳链接点击
  const handleTimestampLinkClick = useCallback((e: MouseEvent) => {
    // 仅处理带有data-timestamp-link属性的元素
    const target = e.target as HTMLElement;
    if (!target.hasAttribute('data-timestamp-link')) return;

    const timestamp = parseFloat(target.getAttribute('data-timestamp-link') || '0');
    if (!isNaN(timestamp) && onTimestampClick) {
      e.preventDefault();
      e.stopPropagation();
      console.log(`Timestamp link clicked: ${timestamp}`);
      onTimestampClick(timestamp);
      return false;
    }
  }, [onTimestampClick]);

  // 辅助函数：设置时间戳链接样式和事件
  const setupTimestampLinks = useCallback((svg: SVGSVGElement) => {
    // 找到所有a元素
    const links = svg.querySelectorAll('a');
    
    links.forEach(link => {
      const href = link.getAttribute('href') || '';
      const timestamp = extractTimestampFromHref(href);
      
      if (timestamp !== null) {
        // 只处理时间戳链接
        link.classList.add('timestamp-link');
        link.setAttribute('data-timestamp-link', timestamp.toString());
        
        // 样式
        link.style.cursor = 'pointer';
        link.style.fill = '#2563eb';
        link.style.fontWeight = 'normal';
        link.style.textDecoration = 'underline';
        
        // 不直接添加点击事件，通过事件委托处理
        
        // 为父元素g添加类
        const parentG = link.closest('g');
        if (parentG) {
          parentG.classList.add('markmap-timestamp-node');
          
          // 处理圆圈样式
          const circle = parentG.querySelector('circle');
          if (circle) {
            circle.setAttribute('fill', darkMode ? '#1e3a8a' : '#dbeafe');
            circle.setAttribute('stroke', '#2563eb');
            circle.setAttribute('stroke-width', '1.5');
          }
        }
      }
    });
  }, [darkMode]);

  useEffect(() => {
    // 确保容器和SVG元素都存在
    if (!containerRef.current || !svgRef.current) return;

    // 设置SVG的初始尺寸
    const container = containerRef.current;
    const svg = svgRef.current;
    svg.setAttribute('width', `${container.clientWidth}px`);
    svg.setAttribute('height', `${container.clientHeight}px`);

    // 创建或更新 Markmap 实例
    const initializeMarkmap = () => {
      if (!mmRef.current) {
        mmRef.current = Markmap.create(svg, {
          nodeMinHeight: 16,
          paddingX: 8,
          duration: 500,
          autoFit: false, // 禁用自动适应，让折叠/展开效果更明显
        });
      }

      try {
        const transformer = new Transformer();
        const { root } = transformer.transform(enhancedMarkdown);
        mmRef.current.setData(root);
        
        setTimeout(() => {
          if (mmRef.current) {
            mmRef.current.fit();
            setIsLoading(false);
          }
        }, 300);
      } catch (error) {
        console.error('Error transforming markdown:', error);
        setIsLoading(false);
      }
    };

    initializeMarkmap();

    // 使用MutationObserver监听DOM变化，动态处理时间戳链接
    if (onTimestampClick) {
      // 先清理之前的观察者
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      // 为整个SVG添加委托的点击事件处理
      svg.addEventListener('click', handleTimestampLinkClick);

      // 创建新的观察者
      observerRef.current = new MutationObserver(() => {
        // 当DOM变化时，重新处理时间戳链接
        setupTimestampLinks(svg);
      });

      // 观察整个SVG元素的子树变化
      observerRef.current.observe(svg, { 
        childList: true, 
        subtree: true,
        attributeFilter: ['href'] // 只在href属性变化时触发
      });

      // 初始设置
      setupTimestampLinks(svg);
    }

    // 处理窗口大小变化
    const handleResize = () => {
      if (svg && container) {
        svg.setAttribute('width', `${container.clientWidth}px`);
        svg.setAttribute('height', `${container.clientHeight}px`);
        mmRef.current?.fit();
      }
    };

    window.addEventListener('resize', handleResize);

    // 清理函数
    return () => {
      window.removeEventListener('resize', handleResize);
      
      // 清理观察者
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      
      // 清理点击事件
      svg.removeEventListener('click', handleTimestampLinkClick);
    };
  }, [enhancedMarkdown, darkMode, onTimestampClick, handleTimestampLinkClick, setupTimestampLinks]);

  const handleZoomIn = () => mmRef.current?.rescale(1.25);
  const handleZoomOut = () => mmRef.current?.rescale(0.8);
  const handleFit = () => mmRef.current?.fit();

  // Enhanced styles for better UI
  const TimestampStyles = () => {
    return (
      <style jsx global>{`
        .timestamp-link {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          cursor: pointer !important;
          fill: hsl(var(--primary)) !important;
          text-decoration: underline !important;
          font-weight: 500 !important;
          text-underline-offset: 2px;
        }
        .markmap-timestamp-node > circle {
          transition: all 0.3s ease;
          filter: drop-shadow(0 2px 4px rgba(0,0,0,0.1));
        }
        .markmap-foreign a {
          color: hsl(var(--primary)) !important;
          transition: all 0.2s ease;
        }
        /* Enhanced node styling */
        .markmap-node {
          transition: all 0.2s ease;
        }
        @media (hover: hover) {
          .timestamp-link:hover {
            fill: hsl(var(--primary)) !important;
            transform: scale(1.05);
            transform-origin: left;
            text-shadow: 0 0 8px hsla(var(--primary), 0.4);
          }
          .markmap-timestamp-node:hover > circle {
            stroke-width: 2px;
            stroke: hsl(var(--primary));
            filter: drop-shadow(0 4px 8px rgba(0,0,0,0.15));
          }
          .markmap-foreign a:hover {
            opacity: 0.8;
            transform: translateY(-1px);
          }
          .markmap-node:hover {
            filter: brightness(1.1);
          }
        }
        /* Improved link styling */
        .markmap-link {
          transition: all 0.3s ease;
        }
        /* Better responsive text sizing */
        @media (max-width: 640px) {
          .markmap-foreign {
            font-size: 12px !important;
          }
        }
        @media (min-width: 641px) {
          .markmap-foreign {
            font-size: 14px !important;
          }
        }
      `}</style>
    );
  };

  return (
    <div 
      ref={containerRef}
      className={cn(
        'relative w-full h-[300px] sm:h-[650px] overflow-hidden rounded-lg border border-border/30',
        darkMode ? 'bg-background dark-mode' : 'bg-background/50',
        className
      )}
    >
      <TimestampStyles />
      
      {/* Interactive Tooltip */}
      {onTimestampClick && (
        <div className="absolute top-2 right-2   bg-background/95 backdrop-blur-sm border border-border/50 rounded-lg p-3 shadow-lg max-w-xs">
          <div className="flex items-start space-x-2">
            <div className="flex items-center justify-center w-6 h-6 bg-primary/10 rounded-full flex-shrink-0 mt-0.5">
              <Info className="h-3 w-3 text-primary" />
            </div>
            <div className="hidden sm:block text-xs space-y-1">
              <div className="font-medium text-foreground">Interactive Mind Map</div>
              <div className="text-muted-foreground leading-relaxed">
                Click on <span className="text-primary font-medium underline">[00:30]</span> to jump to video positions
              </div>
            </div>
          </div>
        </div>
      )}
      
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/50 backdrop-blur-sm">
          <div className="bg-background/90 backdrop-blur-sm border border-border/50 rounded-xl p-6 shadow-lg">
            <div className="flex flex-col items-center gap-4">
              <div className="relative">
                <div className="animate-spin h-8 w-8 border-3 border-primary/30 border-t-primary rounded-full" />
                <div className="absolute inset-0 animate-pulse">
                  <div className="h-8 w-8 bg-primary/10 rounded-full" />
                </div>
              </div>
              <div className="text-center space-y-1">
                <div className="text-sm font-medium text-foreground">Generating Mind Map</div>
                <div className="text-xs text-muted-foreground">Processing episode content...</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <svg 
        ref={svgRef}
        className={cn(
          'w-full h-full',
          darkMode ? '[&_.markmap-node-text]:fill-gray-100' : '[&_.markmap-node-text]:fill-gray-800',
          '[&_.markmap-node-circle]:stroke-none',
          darkMode ? '[&_.markmap-link]:stroke-gray-600' : '[&_.markmap-link]:stroke-gray-300',
          '[&_.markmap-link]:stroke-[1.5px]',
          darkMode ? '[&_.markmap-foreign]:text-white' : '[&_.markmap-foreign]:text-gray-800',
          'transition-colors duration-200'
        )}
      />

      {/* Control Panel */}
      <div className="absolute bottom-4 right-4 bg-background/95 backdrop-blur-sm border border-border/50 rounded-lg p-2 shadow-lg">
        <div className="flex flex-col gap-1">
          <button
            onClick={handleZoomIn}
            className={cn(
              "w-8 h-8 rounded-md flex items-center justify-center group",
              "transition-all duration-200 hover:scale-105",
              "bg-background hover:bg-primary/10 text-muted-foreground hover:text-primary",
              "border border-transparent hover:border-primary/20"
            )}
            title="Zoom In"
          >
            <ZoomIn className="h-4 w-4" />
          </button>
          <button
            onClick={handleZoomOut}
            className={cn(
              "w-8 h-8 rounded-md flex items-center justify-center group",
              "transition-all duration-200 hover:scale-105",
              "bg-background hover:bg-primary/10 text-muted-foreground hover:text-primary",
              "border border-transparent hover:border-primary/20"
            )}
            title="Zoom Out"
          >
            <ZoomOut className="h-4 w-4" />
          </button>
          <button
            onClick={handleFit}
            className={cn(
              "w-8 h-8 rounded-md flex items-center justify-center group",
              "transition-all duration-200 hover:scale-105",
              "bg-background hover:bg-primary/10 text-muted-foreground hover:text-primary",
              "border border-transparent hover:border-primary/20"
            )}
            title="Fit to Screen"
          >
            <Maximize2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
